import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiCheck, HiX, HiChevronUp, HiChevronDown } from 'react-icons/hi';
import { challengeService } from '../../services/challengeService';
import { supabase } from '../../supabaseClient';
import './styles/Modal.css';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import ExpandableText from './ExpandableText';

function CigsidaChallengeManagement({ selectedEdition }) {
  const [challengeData, setChallengeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (selectedEdition) loadData();
  }, [selectedEdition]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await challengeService.getChallengeData(selectedEdition);
      setChallengeData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMain = (field, value) => {
    setChallengeData(prev => ({
      ...prev,
      main: { ...prev.main, [field]: value }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveMain = async () => {
    try {
      await challengeService.updateChallengeData(challengeData.main, selectedEdition);
      setHasUnsavedChanges(false);
      showSuccess();
    } catch (e) {
      console.error(e);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const showSuccess = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleAddMember = () => {
    setSelectedMember(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleRowReorder = async (e) => {
    // e.value contains the reordered array
    const reorderedMembers = e.value.map((member, index) => ({
      ...member,
      display_order: index + 1 // Start from 1
    }));
    
    setChallengeData(prev => ({
      ...prev,
      members: reorderedMembers
    }));

    try {
      await challengeService.updateMembersOrder(reorderedMembers);
      showSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la mise à jour de l\'ordre.');
    }
  };

  const handleDeleteMember = async (id) => {
    if (window.confirm('Voulez-vous supprimer ce membre ?')) {
      await challengeService.deleteMember(id);
      loadData();
      showSuccess();
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const path = `${selectedEdition}/challenge/${Date.now()}-${file.name}`;
        const { error } = await supabase.storage.from('speaker-images').upload(path, file);
        if (error) throw error;
        const { data: publicUrlData } = supabase.storage.from('speaker-images').getPublicUrl(path);
        handleUpdateMain(field, publicUrlData.publicUrl);
      } catch (e) {
        console.error(e);
      }
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="admin-section">
      {showSaveSuccess && <div className="save-success-message"><HiCheck /> Sauvegardé !</div>}
      
      <h3>Configuration Globale Challenge</h3>
      <div className="form-group">
        <label>Motto:</label>
        <input type="text" value={challengeData.main.motto} onChange={e => handleUpdateMain('motto', e.target.value)} />
      </div>

      <div className="form-group">
        <label>Objectif:</label>
        <textarea value={challengeData.main.objective} onChange={e => handleUpdateMain('objective', e.target.value)} />
      </div>

      <div className="form-group">
        <label>Catégories:</label>
        <textarea value={challengeData.main.categories} onChange={e => handleUpdateMain('categories', e.target.value)} />
      </div>

      <div className="form-group">
        <label>Prix décernés:</label>
        <textarea value={challengeData.main.prizes} onChange={e => handleUpdateMain('prizes', e.target.value)} />
      </div>

      <div className="form-group">
        <label>Esprit challenge:</label>
        <textarea value={challengeData.main.competition_spirit} onChange={e => handleUpdateMain('competition_spirit', e.target.value)} />
      </div>

      <div className="form-group">
        <label>Image Footer (A4):</label>
        <input type="file" onChange={e => handleFileUpload(e, 'footer_image_url')} />
      </div>
      
      <div className="form-group">
        <label>PDF Link (Open on click):</label>
        <input type="file" onChange={e => handleFileUpload(e, 'footer_pdf_url')} />
      </div>

      {hasUnsavedChanges && <button onClick={handleSaveMain} className="btn-primary">Sauvegarder Configuration</button>}

      <h3>Gestion des Membres Challenge</h3>
      <button className="add-btn" onClick={handleAddMember}><HiPlus /> Ajouter Membre</button>
      
      <div className="card">
        <DataTable 
          value={challengeData.members} 
          reorderableRows
          onRowReorder={handleRowReorder}
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucun membre trouvé."
        >
          <Column rowReorder style={{ width: '3rem' }} />
          <Column 
            header="Aperçu" 
            body={(rowData) => (
              <div className="flex justify-content-center">
                <img 
                  src={rowData.image_url || 'https://via.placeholder.com/50'} 
                  alt={rowData.name} 
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} 
                />
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="name" 
            header="Nom" 
            sortable 
            body={(rowData) => <strong>{rowData.name}</strong>}
            style={{ whiteSpace: 'nowrap' }}
          />
          <Column 
            field="title" 
            header="Titre" 
            body={(rowData) => <ExpandableText text={rowData.title || ''} maxChars={30} />}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="organization" 
            header="Organisation" 
            body={(rowData) => <ExpandableText text={rowData.organization || ''} maxChars={30} />}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="display_order" 
            header="Ordre" 
            sortable 
            body={(rowData) => <span className="p-badge p-badge-info">{rowData.display_order}</span>}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <div className="flex gap-2">
                <Button icon="pi pi-pencil" onClick={() => handleEditMember(rowData)} rounded outlined severity="info" size="small" />
                <Button icon="pi pi-trash" onClick={() => handleDeleteMember(rowData.id)} rounded outlined severity="danger" size="small" />
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>

      {showModal && (
        <ChallengeMemberModal
          mode={modalMode}
          member={selectedMember}
          onClose={() => setShowModal(false)}
          onSave={async (formData) => {
            if (modalMode === 'add') await challengeService.addMember(formData, selectedEdition);
            else await challengeService.updateMember({ ...formData, id: selectedMember.id });
            loadData();
            setShowModal(false);
            showSuccess();
          }}
          selectedEdition={selectedEdition}
        />
      )}
    </div>
  );
}

function ChallengeMemberModal({ mode, member, onClose, onSave, selectedEdition }) {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    title: member?.title || '',
    organization: member?.organization || '',
    image_url: member?.image_url || '',
    display_order: member?.display_order || 0
  });

  const [availableSpeakers, setAvailableSpeakers] = useState([]);
  const [loadingSpeakers, setLoadingSpeakers] = useState(false);

  useEffect(() => {
    loadSpeakers();
  }, []);

  const loadSpeakers = async () => {
    try {
      setLoadingSpeakers(true);
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', selectedEdition)
        .single();
      
      if (edition) {
        const { data } = await supabase
          .from('speakers')
          .select('*')
          .eq('edition_id', edition.id)
          .order('name');
        setAvailableSpeakers(data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSpeakers(false);
    }
  };

  const handleSelectSpeaker = (e) => {
    const speakerId = e.target.value;
    if (!speakerId) return;

    const speaker = availableSpeakers.find(s => s.id === parseInt(speakerId));
    if (speaker) {
      setFormData({
        ...formData,
        name: speaker.name || '',
        title: speaker.title || '',
        organization: speaker.organization || '',
        image_url: speaker.image_url || ''
      });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const path = `${selectedEdition}/challenge/${Date.now()}-${file.name}`;
      await supabase.storage.from('speaker-images').upload(path, file);
      const { data: publicUrlData } = supabase.storage.from('speaker-images').getPublicUrl(path);
      setFormData({ ...formData, image_url: publicUrlData.publicUrl });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'add' ? 'Ajouter Membre' : 'Modifier Membre'}</h2>
          <button onClick={onClose}><HiX /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(formData); }} className="modal-form">
          <div className="modal-body">
            {mode === 'add' && (
              <div className="form-group">
                <label>Copier depuis un Intervenant existant:</label>
                <select onChange={handleSelectSpeaker} defaultValue="">
                  <option value="">-- Sélectionner un intervenant --</option>
                  {availableSpeakers.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.organization})</option>
                  ))}
                </select>
                <hr style={{ margin: '15px 0', border: '0', borderTop: '1px solid #eee' }} />
              </div>
            )}
            <div className="form-group"><label>Nom</label><input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required /></div>
            <div className="form-group"><label>Titre</label><input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} /></div>
            <div className="form-group"><label>Organisation</label><input type="text" value={formData.organization} onChange={e => setFormData({ ...formData, organization: e.target.value })} /></div>
            <div className="form-group">
              <label>Photo</label>
              <input type="file" onChange={handleImageUpload} />
              {formData.image_url && <img src={formData.image_url} alt="preview" style={{ width: 50, marginTop: 5 }} />}
            </div>
            <div className="form-group">
              <label>Ordre d'affichage (1 pour Top Center)</label>
              <input type="number" value={formData.display_order} onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-primary">Valider</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CigsidaChallengeManagement;
